export class AzureConstants {
    /**
     * Azure Resource Manager endpoint - Azure Global/Public cloud.
     */
    public static readonly azureGlobalArmEndpoint = 'https://management.azure.com';

    /**
     * Azure public portal.
     */
    public static readonly azureGlobalPortal = 'https://portal.azure.com';

    /**
     * Azure Resource Manager endpoint - Azure China cloud.
     */
    public static readonly azureChinaArmEndpoint = 'https://management.chinacloudapi.cn';

    /**
     * Azure China portal.
     */
    public static readonly azureChinaPortal = 'https://portal.azure.cn';

    /**
     * Azure Resource Manager endpoint - Azure US Govenment/Fairfax/Arlington.
     */
     public static readonly azureUSGovArmEndpoint = 'https://management.usgovcloudapi.net';

    /**
     * Azure US Government portal.
     */
    public static readonly azureUSGovPortal = 'https://portal.azure.us';

    public static readonly armApiVersion = '?api-version=2016-06-01';
    public static readonly azureSubscriptionUrl = '{0}/subscriptions?api-version=2016-06-01';
    public static readonly armDeploymentUrl =
        '{0}/subscriptions/{1}/resourcegroups/{2}/providers/Microsoft.Resources/deployments/{3}?api-version=2018-02-01';
    public static readonly validateAzureTemplate =
        '{0}/subscriptions/{1}/resourcegroups/{2}/providers/Microsoft.Resources/deployments/{3}/validate?api-version=2018-05-01';
    public static readonly getAllGeoLocations = '{0}/subscriptions/{1}/locations?api-version=2016-06-01';
    public static readonly getResourceGroupsUri = '{0}/subscriptions/{1}/resourcegroups?api-version=2018-02-01';

    // Resource groups.
    public static readonly listResourceGroups = '{0}/subscriptions/{1}/resourcegroups?api-version=2018-05-01';
    public static readonly listResourceGroupsWithParameters =
        '{0}/subscriptions/{1}/resourcegroups?$filter={2}&$top={3}api-version=2018-05-01';
    public static readonly resourceGroupUrl = '{0}/subscriptions/{1}/resourcegroups/{2}?api-version=2018-05-01';

    // Template deployment urls.
    public static readonly ArmDeploymentUrl =
        '{0}/subscriptions/{1}/resourcegroups/{2}/providers/Microsoft.Resources/deployments/{3}?api-version=2018-02-01';

    // Request header keys and values
    public static readonly bearer = 'Bearer ';
    public static readonly authorizationHeaderKey = 'Authorization';
    public static readonly acceptLanguageHeaderKey = 'Accept-Language';
    public static readonly clientRequestHeaderKey = 'x-ms-client-request-id';
    public static readonly returnClientRequestHeaderKey = 'x-ms-return-client-request-id';

    // TODO: Remove work around for telemetry. Client request id shouldnt have any parameters.
    public static readonly telemetryDataParameter = 'telemetryData={0}; ';
    public static readonly telemetryDataInvalidValue = 'invalid';
    public static readonly integrationTypeDataParameter = 'integrationType={0}; ';
    public static readonly clientRequestIdQualifier = '{0}; ';
    public static readonly sourceParameter = 'source=WindowsAdminCenter';
    public static readonly maxEncodedTelemetryDataLength = 512;
    public static readonly resourceDeploymentSubstring = 'Microsoft.Resources/deployments';

    // ms-graph resource endpoint
    public static readonly graphResourceEndpoint = '/providers/Microsoft.ResourceGraph/resources?api-version=2021-03-01';

    // ms-graph query
    public static readonly resourceTypeFilterQuery = `resources 
    | extend parsedProperties = parse_json(properties) 
    | extend computerName = parsedProperties.osProfile.computerName 
    | where (type == \"microsoft.hybridcompute/machines\" or type == \"microsoft.compute/virtualmachines\") and tolower(computerName) in ({0}) 
    | project arcStatus = properties.status,  nodeName = computerName, type`;
    public static readonly azureVmsFilterQuery = `resources 
    | extend parsedProperties = parse_json(properties) 
    | extend computerName = parsedProperties.osProfile.computerName 
    | where (type == "microsoft.compute/virtualmachines") and tolower(computerName) in ({0})
    | project nodeName = computerName, type`;

    public static readonly virtualMachine = "microsoft.compute/virtualmachines";

     /**
     * WAC URLS for azure arc machine agent
     */
     public static readonly arcMachineAgentIssuesURL = "https://go.microsoft.com/fwlink/?linkid=2248533";
     public static readonly expiredArcMachineAgentURL = "https://learn.microsoft.com/azure/azure-arc/servers/manage-agent?tabs=windows#step-2-disconnect-the-server-from-azure-arc";

     /**
     * The interval of running the graph query call.
     * In this case it's 15 minutes
     */
     public static graphQueryInterval = 900000;
     public static readonly arcStatusGraphQueryDelay = 7000;
     public static readonly arcStatusGraphQueryRetryCount = 8;
}

/*
 * Azure integration type is used to distinguish which Azure tool/service in Windows Admin Center is making a
 * Azure resource management (ARM) call. If only one repository has one tool/service, this will be inferred based
 * on the name of the module. If a repository has more than one tool/service then the integration type needs to be
 * explicitly set when calling this.appContextService.azureManagerService.createRequestWithHeaders() or when a
 * method like this.appContextService.azureManagerService.getSubscriptions() is called.
 */
export enum AzureIntegrationType {
    Backup = 'Backup',
    Bridge = 'Bridge',
    FileSync = 'FileSync',
    HybridServices = 'HybridServices',
    Monitor = 'Monitor',
    NetworkAdapter = 'NetworkAdapter',
    SecurityCenter = 'SecurityCenter',
    SiteRecovery = 'SiteRecovery',
    AzureMigrate = 'AzureMigrate',
    SubnetStretch = 'SubnetStretch',
    S2SVPN = 'S2SVPN',
    UpdateManagement = 'UpdateManagement',
    VirtualMachineCreation = 'VirtualMachineCreation',
    VirtualMachineSelection = 'VirtualMachineSelection'
}

export namespace ArmTelemetry {
    export function toIntegrationType(moduleName: string): string {
        switch (moduleName) {
            case 'msft.azure.azure-backup': return AzureIntegrationType.Backup;
            case 'msft.sme.azure-file-sync': return AzureIntegrationType.FileSync;
            case 'msft.sme.azure-center': return AzureIntegrationType.HybridServices;
            case 'msft.sme.server-manager': return AzureIntegrationType.Monitor;
            case 'msft.sme.network-settings': return AzureIntegrationType.NetworkAdapter;
            case 'msft.azure.security-center': return AzureIntegrationType.SecurityCenter;
            case 'msft.sme.hyperv': return AzureIntegrationType.SiteRecovery;
            case 'msft.sme.subnet-stretch': return AzureIntegrationType.SubnetStretch;
            case 'msft.sdn.vgw-network-connection': return AzureIntegrationType.S2SVPN;
            case 'msft.sme.windows-update': return AzureIntegrationType.UpdateManagement;
            default: return moduleName;
        }
    }
}
